import {
  Checkbox,
  DatePicker,
  FormField,
  Input,
  NumberStepper,
  Select,
  Text,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { isQueued } from '../../offline/offline';
import {
  useColonies,
  useRecordTreatment,
  useTreatmentMethods,
  useUpdateTreatment,
  type Treatment,
  type TreatmentApplication,
  type TreatmentFields,
  type TreatmentMethod,
} from '../api';
import { ColonyPicker } from './ColonyPicker';
import styles from './forms.module.css';

const APPLICATIONS: TreatmentApplication[] = [
  'drip',
  'spray',
  'evaporate',
  'sublimate',
  'strip',
  'biotechnical',
];
const CUSTOM = 'custom';

interface Form {
  method: string;
  methodName: string;
  startedOn: string;
  endedOn: string;
  longTerm: boolean;
  amount: number | null;
  unit: string;
  application: string;
  batch: string;
  source: string;
  waitingDays: number | null;
  notes: string;
}

function fromTreatment(t: Treatment): Form {
  return {
    method: t.method_id ?? CUSTOM,
    methodName: t.method_name,
    startedOn: t.started_on,
    endedOn: t.ended_on ?? '',
    longTerm: t.long_term,
    amount: t.amount ?? null,
    unit: t.unit ?? '',
    application: t.application ?? '',
    batch: t.medication_batch ?? '',
    source: t.source ?? '',
    waitingDays: t.waiting_days,
    notes: t.notes ?? '',
  };
}

function withMethod(form: Form, m: TreatmentMethod | undefined): Form {
  if (!m) return { ...form, method: CUSTOM };
  return {
    ...form,
    method: m.id,
    methodName: m.name,
    longTerm: m.long_term,
    amount: m.default_amount ?? null,
    unit: m.unit ?? '',
    application: m.application ?? '',
    waitingDays: m.waiting_days,
  };
}

export interface TreatmentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Edit this treatment; otherwise record a new one. */
  treatment?: Treatment;
  /** Preselected colonies when recording. */
  colonyIds?: string[];
  onRecorded?: () => void;
}

/** Records a treatment for several colonies (TRT-001) or edits one (TRT-003). */
export function TreatmentDialog({
  open,
  onClose,
  treatment,
  colonyIds,
  onRecorded,
}: TreatmentDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const methods = useTreatmentMethods(false, open);
  const colonies = useColonies({ status: 'active' }, open && !treatment);
  const record = useRecordTreatment();
  const update = useUpdateTreatment();
  const mutation = treatment ? update : record;
  const [selected, setSelected] = useState<string[]>(colonyIds ?? []);
  const [form, setForm] = useState<Form>(() =>
    treatment
      ? fromTreatment(treatment)
      : {
          method: '',
          methodName: '',
          startedOn: todayIso(),
          endedOn: '',
          longTerm: false,
          amount: null,
          unit: '',
          application: '',
          batch: '',
          source: '',
          waitingDays: 0,
          notes: '',
        },
  );
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const errors = formErrors(mutation.error, {
    colonies: ['colonies.'],
    method: ['treatment.method_required', 'treatment_method.', 'treatment.method_name'],
    endedOn: ['treatment.ends_before_start'],
    amount: ['quantity.'],
    waitingDays: ['treatment.waiting_days'],
  });
  const close = () => {
    mutation.reset();
    onClose();
  };

  const submit = () => {
    const body: TreatmentFields = {
      method_id: form.method && form.method !== CUSTOM ? form.method : null,
      method_name: form.method === CUSTOM || !form.method ? form.methodName : null,
      started_on: form.startedOn,
      ended_on: form.endedOn || null,
      long_term: form.longTerm,
      amount: form.amount,
      unit: form.unit || null,
      application: (form.application || null) as TreatmentApplication | null,
      medication_batch: form.batch || null,
      source: form.source || null,
      waiting_days: form.waitingDays ?? 0,
      notes: form.notes || null,
    };
    if (treatment) {
      update.mutate(
        { id: treatment.id, body, version: treatment.version },
        {
          onSuccess: () => {
            toast.show(t('apiary.treatment.saved'));
            close();
          },
        },
      );
    } else {
      record.mutate(
        { ...body, colony_ids: selected },
        {
          onSuccess: (r) => {
            if (r.some(isQueued)) toast.show(t('offline.captured'), 'info');
            else toast.show(t('apiary.treatment.recorded', { count: r.length }));
            onRecorded?.();
            close();
          },
        },
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={treatment ? t('apiary.treatment.editTitle') : t('apiary.treatment.recordTitle')}
      description={treatment?.colony.name}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={mutation.isPending}
      error={errors.general}
    >
      {!treatment && (
        <ColonyPicker
          colonies={colonies.data ?? []}
          selected={selected}
          onChange={setSelected}
          error={errors.field.colonies}
          required
        />
      )}
      <FormField label={t('apiary.treatment.method')} required error={errors.field.method}>
        {(p) => (
          <Select
            {...p}
            value={form.method}
            onChange={(e) =>
              setForm((f) =>
                withMethod(
                  f,
                  methods.data?.find((m) => m.id === e.target.value),
                ),
              )
            }
            options={[
              { value: '', label: t('apiary.treatment.choose'), disabled: true },
              ...(methods.data ?? []).map((m) => ({ value: m.id, label: m.name })),
              { value: CUSTOM, label: t('apiary.treatment.custom') },
            ]}
          />
        )}
      </FormField>
      {form.method === CUSTOM && (
        <FormField label={t('apiary.treatment.methodName')} required>
          {(p) => (
            <Input
              {...p}
              value={form.methodName}
              maxLength={100}
              onChange={(e) => set('methodName', e.target.value)}
            />
          )}
        </FormField>
      )}
      <div className={styles.row}>
        <FormField label={t('apiary.treatment.startedOn')} required>
          {(p) => (
            <DatePicker {...p} value={form.startedOn} onChange={(v) => set('startedOn', v)} />
          )}
        </FormField>
        {(form.longTerm || form.endedOn) && (
          <FormField label={t('apiary.treatment.endedOn')} error={errors.field.endedOn}>
            {(p) => (
              <DatePicker
                {...p}
                value={form.endedOn}
                min={form.startedOn}
                onChange={(v) => set('endedOn', v)}
              />
            )}
          </FormField>
        )}
      </div>
      <Checkbox
        label={t('apiary.treatment.longTerm')}
        description={t('apiary.treatment.longTermHint')}
        checked={form.longTerm}
        onChange={(e) => set('longTerm', e.target.checked)}
      />
      <div className={styles.row}>
        <FormField label={t('apiary.treatment.amount')} error={errors.field.amount}>
          {(p) => (
            <NumberStepper
              {...p}
              value={form.amount}
              onChange={(v) => set('amount', v)}
              min={0}
              decimals={3}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('apiary.treatment.unit')}>
          {(p) => (
            <Input
              {...p}
              value={form.unit}
              maxLength={20}
              onChange={(e) => set('unit', e.target.value)}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('apiary.treatment.application')}>
        {(p) => (
          <Select
            {...p}
            value={form.application}
            onChange={(e) => set('application', e.target.value)}
            options={[
              { value: '', label: t('apiary.treatment.noApplication') },
              ...APPLICATIONS.map((a) => ({ value: a, label: t(`apiary.application.${a}`) })),
            ]}
          />
        )}
      </FormField>
      <fieldset className={styles.section}>
        <legend>
          <Text as="span" size="sm" weight="semibold">
            {t('apiary.treatment.logbook')}
          </Text>
        </legend>
        <div className={styles.row}>
          <FormField label={t('apiary.treatment.batch')}>
            {(p) => (
              <Input
                {...p}
                value={form.batch}
                maxLength={50}
                onChange={(e) => set('batch', e.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('apiary.treatment.waitingDays')} error={errors.field.waitingDays}>
            {(p) => (
              <NumberStepper
                {...p}
                value={form.waitingDays}
                onChange={(v) => set('waitingDays', v)}
                min={0}
                max={365}
                labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
              />
            )}
          </FormField>
        </div>
        <FormField label={t('apiary.treatment.source')}>
          {(p) => (
            <Input
              {...p}
              value={form.source}
              maxLength={100}
              onChange={(e) => set('source', e.target.value)}
            />
          )}
        </FormField>
      </fieldset>
      <FormField label={t('apiary.treatment.notes')}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
